import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '../store';
import {
  WorkflowAssignment,
  Workflow,
  Asset,
  CreateAssignmentRequest,
  TeamMemberInfo,
  WorkflowNodeData,
  Dashboard as DashboardType,
  TaskProgress,
} from '../types';
import { WidgetTemplate } from '../components/widgets/types';
import { widgetRegistry } from '../components/widgets/registry';
import type { WidgetType } from '../components/widgets/types';
import {
  teamService,
  workflowService,
  assignmentService,
  assetService,
  dashboardService,
} from '../services';
import { widgetService } from '../services/widgetService';
import { useToast } from '../hooks/useToast';
import NewAssignmentModal from '../components/assignments/NewAssignmentModal';
import EditAssignmentModal from '../components/assignments/EditAssignmentModal';
import CopyAssignmentModal from '../components/assignments/CopyAssignmentModal';
import PageTopBar from '../components/ui/PageTopBar';
import SearchBar from '../components/ui/SearchBar';
import SearchableSelect from '../components/ui/SearchableSelect';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import ConfirmationModal from '../components/common/ConfirmationModal';

// ---- Types ----

interface SummaryWidgetInfo {
  widgetId: string;
  title: string;
  widgetType: WidgetType | '';
  dataKey: string;
  stepId: string;
  dashboardId: string;
  templateId: string;
}

interface Filters {
  search: string;
  status: string;
  workflowId: string;
  workerId: string;
  assetId: string;
}

const EMPTY_FILTERS: Filters = {
  search: '',
  status: '',
  workflowId: '',
  workerId: '',
  assetId: '',
};

type SortField = 'assignee' | 'asset' | 'progress' | 'due';
type SortDir = 'asc' | 'desc';

// ---- Constants ----

const statusVariants: Record<string, 'warning' | 'primary' | 'success' | 'secondary'> = {
  pending: 'warning',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'secondary',
};

const STATUS_ORDER: Record<string, number> = {
  pending: 0,
  in_progress: 1,
  completed: 2,
  cancelled: 3,
};

// ---- Helpers ----

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const futureDays = Math.abs(diffDays);
    if (futureDays === 1) return 'Tomorrow';
    if (futureDays < 7) return `In ${futureDays} days`;
    return date.toLocaleDateString();
  }

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

const getProgressColor = (progress: number) => {
  if (progress >= 100) return 'bg-success-500';
  if (progress >= 50) return 'bg-primary-500';
  if (progress >= 25) return 'bg-warning-500';
  return 'bg-neutral-300';
};

// ---- localStorage helpers ----

const loadCollapsed = (): Set<string> => {
  try {
    const v = localStorage.getItem('dashboard_collapsed');
    if (v) return new Set(JSON.parse(v));
  } catch { /* ignore */ }
  return new Set();
};

const saveCollapsed = (set: Set<string>) => {
  try { localStorage.setItem('dashboard_collapsed', JSON.stringify(Array.from(set))); } catch { /* ignore */ }
};

// ---- Component ----

const UnifiedDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const toast = useToast();

  const isOwner = user?.role === 'owner' || user?.role === 'admin';

  // --- Core data ---
  const [assignments, setAssignments] = useState<WorkflowAssignment[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberInfo[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);

  // --- Summary data (lazy) ---
  const [dashboards, setDashboards] = useState<Map<string, DashboardType>>(new Map());
  const [templates, setTemplates] = useState<Map<string, WidgetTemplate>>(new Map());
  const [progressMap, setProgressMap] = useState<Map<string, TaskProgress[]>>(new Map());
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // --- UI state ---
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(loadCollapsed);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sortField, setSortField] = useState<SortField>('assignee');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // --- Modal state ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<WorkflowAssignment | null>(null);
  const [copyingAssignment, setCopyingAssignment] = useState<WorkflowAssignment | null>(null);
  const [deletingAssignmentId, setDeletingAssignmentId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // --- Kebab menu ---
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  // --- Peek modal (summary) ---
  const [peekModal, setPeekModal] = useState<{
    assignmentId: string;
    widget: SummaryWidgetInfo;
    assignment: WorkflowAssignment;
  } | null>(null);

  // ---- Data loading ----

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (isOwner) {
        const [membersData, workflowsData, assignmentsData, assetsData] = await Promise.all([
          teamService.getTeamMembers(),
          workflowService.getWorkflows(1, 100),
          assignmentService.getAssignments(1, 100),
          assetService.getAssets(1, 100),
        ]);
        setTeamMembers(membersData);
        setWorkflows(workflowsData.data);
        setAssignments(assignmentsData.data);
        setAssets(assetsData.data);
      } else {
        const assignmentsData = await assignmentService.getAssignments(1, 100);
        setAssignments(assignmentsData.data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lazy-load summary data when switching to summary mode
  const loadSummaryData = useCallback(async () => {
    if (summaryLoaded || summaryLoading || assignments.length === 0) return;
    setSummaryLoading(true);
    try {
      // Collect dashboard IDs from workflow nodes
      const dashboardIds = new Set<string>();
      for (const a of assignments) {
        const nodes = a.workflow?.flowData?.nodes || [];
        for (const node of nodes) {
          const nodeData = node.data as WorkflowNodeData;
          if (nodeData?.dashboardId) dashboardIds.add(nodeData.dashboardId);
        }
      }

      // Fetch dashboards
      const dashEntries = await Promise.all(
        Array.from(dashboardIds).map(async (id) => {
          try {
            const dash = await dashboardService.getDashboard(id);
            return [id, dash] as [string, DashboardType];
          } catch { return null; }
        }),
      );
      const dashMap = new Map<string, DashboardType>();
      for (const entry of dashEntries) {
        if (entry) dashMap.set(entry[0], entry[1]);
      }
      setDashboards(dashMap);

      // Collect template IDs from summary widgets
      const templateIds = new Set<string>();
      for (const dash of Array.from(dashMap.values())) {
        for (const w of dash.widgets || []) {
          if (w.isSummary && w.templateId) templateIds.add(w.templateId);
        }
      }

      // Fetch templates
      const templateEntries = await Promise.all(
        Array.from(templateIds).map(async (id) => {
          try {
            const tmpl = await widgetService.getWidgetTemplate(id);
            return [id, tmpl] as [string, WidgetTemplate];
          } catch { return null; }
        }),
      );
      const tmplMap = new Map<string, WidgetTemplate>();
      for (const entry of templateEntries) {
        if (entry) tmplMap.set(entry[0], entry[1]);
      }
      setTemplates(tmplMap);

      // Fetch progress for all assignments
      const progressEntries = await Promise.all(
        assignments.map(async (a) => {
          try {
            const progress = await assignmentService.getProgress(a.id);
            return [a.id, progress] as [string, TaskProgress[]];
          } catch { return [a.id, []] as [string, TaskProgress[]]; }
        }),
      );
      setProgressMap(new Map(progressEntries));

      setSummaryLoaded(true);
    } catch (err) {
      console.error('Failed to load summary data:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, [assignments, summaryLoaded, summaryLoading]);

  useEffect(() => {
    if (isOwner && !summaryLoaded && assignments.length > 0) {
      loadSummaryData();
    }
  }, [isOwner, summaryLoaded, assignments, loadSummaryData]);

  // ---- Kebab menu outside-click ----

  const openMenu = useCallback((assignmentId: string, buttonEl: HTMLButtonElement) => {
    if (openMenuId === assignmentId) {
      setOpenMenuId(null);
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.right - 144 });
    setOpenMenuId(assignmentId);
  }, [openMenuId]);

  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuId]);

  // ---- Collapse persistence ----

  const toggleCollapse = (wfId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(wfId)) next.delete(wfId);
      else next.add(wfId);
      saveCollapsed(next);
      return next;
    });
  };

  // ---- Progress map ----

  const detailsProgressMap = useMemo(() => {
    const map = new Map<string, { progress: number; currentStep: string }>();
    for (const assignment of assignments) {
      const allNodes = assignment.workflow?.flowData?.nodes || [];
      const workSteps = allNodes.filter(
        (node: any) => node.id !== 'start' && node.type !== 'input' && node.type !== 'output',
      );
      const totalSteps = workSteps.length || 1;
      let progress = 0;
      let currentStep = 'Not Started';
      if (assignment.status === 'completed') {
        progress = 100;
        currentStep = 'Completed';
      } else if (assignment.status === 'in_progress') {
        progress = 50;
        currentStep = `Step ${Math.floor(totalSteps / 2) + 1} of ${totalSteps}`;
      }
      map.set(assignment.id, { progress, currentStep });
    }
    return map;
  }, [assignments]);

  // ---- Filter options ----

  const uniqueWorkflows = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assignments) {
      if (a.workflow?.name) map.set(a.workflowId, a.workflow.name);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [assignments]);

  const uniqueAssignees = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assignments) {
      if (a.worker?.fullName) map.set(a.workerId, a.worker.fullName);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [assignments]);

  const uniqueAssets = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assignments) {
      if (a.assetId && a.asset?.name) map.set(a.assetId, a.asset.name);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [assignments]);

  // ---- Filtering ----

  const hasActiveFilters =
    filters.search || filters.status || filters.workflowId || filters.workerId || filters.assetId;

  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const match =
          a.workflow?.name?.toLowerCase().includes(q) ||
          a.worker?.fullName?.toLowerCase().includes(q) ||
          a.worker?.email?.toLowerCase().includes(q) ||
          a.asset?.name?.toLowerCase().includes(q) ||
          a.asset?.sector?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filters.status && a.status !== filters.status) return false;
      if (filters.workflowId && a.workflowId !== filters.workflowId) return false;
      if (filters.workerId && a.workerId !== filters.workerId) return false;
      if (filters.assetId && a.assetId !== filters.assetId) return false;
      return true;
    });
  }, [assignments, filters]);

  // ---- Sorting ----

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'due' || field === 'progress' ? 'desc' : 'asc');
    }
  };

  const sortAssignments = useCallback((list: WorkflowAssignment[]): WorkflowAssignment[] => {
    const sorted = [...list];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'assignee':
          cmp = (a.worker?.fullName || '').localeCompare(b.worker?.fullName || '');
          break;
        case 'asset':
          cmp = (a.asset?.name || '').localeCompare(b.asset?.name || '');
          break;
        case 'progress':
          cmp = (detailsProgressMap.get(a.id)?.progress || 0) - (detailsProgressMap.get(b.id)?.progress || 0);
          break;
        case 'due':
          cmp = new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [sortField, sortDir, detailsProgressMap]);

  // ---- Group by workflow ----

  const workflowGroups = useMemo(() => {
    const groups = new Map<string, { workflowName: string; assignments: WorkflowAssignment[] }>();
    for (const a of filteredAssignments) {
      const wfId = a.workflowId;
      const wfName = a.workflow?.name || 'Unknown Workflow';
      if (!groups.has(wfId)) {
        groups.set(wfId, { workflowName: wfName, assignments: [] });
      }
      groups.get(wfId)!.assignments.push(a);
    }
    return groups;
  }, [filteredAssignments]);

  // ---- Summary widget extraction ----

  const getSummaryWidgets = useCallback((assignment: WorkflowAssignment): SummaryWidgetInfo[] => {
    const nodes = assignment.workflow?.flowData?.nodes || [];
    const results: SummaryWidgetInfo[] = [];

    for (const node of nodes) {
      const nodeData = node.data as WorkflowNodeData;
      const dashboardId = nodeData?.dashboardId;
      if (!dashboardId) continue;

      const dash = dashboards.get(dashboardId);
      if (!dash?.widgets) continue;

      for (const widget of dash.widgets) {
        if (!widget.isSummary) continue;

        const tmpl = templates.get(widget.templateId);
        const stepWidgetConfig = nodeData?.widgetConfigs?.widgets?.[widget.id];

        const dataKey =
          (stepWidgetConfig?.dataKey as string) ||
          (widget.configOverrides as Record<string, unknown>)?.dataKey as string ||
          widget.id;

        results.push({
          widgetId: widget.id,
          title: widget.title || tmpl?.name || 'Summary',
          widgetType: tmpl?.type || '',
          dataKey,
          stepId: node.id,
          dashboardId,
          templateId: widget.templateId,
        });
      }
    }

    return results;
  }, [dashboards, templates]);

  const getWidgetData = useCallback((assignmentId: string, sw: SummaryWidgetInfo): unknown => {
    const progress = progressMap.get(assignmentId);
    if (!progress) return undefined;
    const stepProgress = progress.find((p) => p.stepId === sw.stepId);
    if (!stepProgress?.data) return undefined;
    return stepProgress.data[sw.dataKey];
  }, [progressMap]);

  const renderSummaryCell = useCallback((assignmentId: string, sw: SummaryWidgetInfo, assignment?: WorkflowAssignment) => {
    const data = getWidgetData(assignmentId, sw);
    const def = sw.widgetType ? widgetRegistry.get(sw.widgetType as WidgetType) : undefined;
    const SummaryComponent = def?.Summary;

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (assignment) {
        setPeekModal({ assignmentId, widget: sw, assignment });
      }
    };

    const content = SummaryComponent
      ? <SummaryComponent data={data} config={{}} />
      : data === undefined || data === null
        ? <span className="text-neutral-300">—</span>
        : <span className="text-sm text-neutral-600">{String(data)}</span>;

    return (
      <button onClick={handleClick} className="hover:opacity-70 transition-opacity" title="View details">
        {content}
      </button>
    );
  }, [getWidgetData]);

  // ---- CRUD handlers ----

  const handleCreateAssignments = async (data: CreateAssignmentRequest) => {
    setCreating(true);
    try {
      const createPromises: Promise<WorkflowAssignment>[] = [];
      let failedCount = 0;
      let duplicateCount = 0;

      const addAssignment = (workerId: string, workflowId: string, assetId?: string) => {
        createPromises.push(
          assignmentService
            .createAssignment({
              workflowId,
              workerId,
              assetId,
              dueDate: data.dueDate || undefined,
              widgetData: data.widgetData,
            })
            .catch(err => {
              if (err.response?.status === 409) {
                duplicateCount++;
              } else {
                failedCount++;
              }
              throw err;
            }),
        );
      };

      if (data.memberAssets) {
        for (const workerId of data.workerIds) {
          const workerAssetIds = data.memberAssets[workerId] || [];
          for (const workflowId of data.workflowIds) {
            if (workerAssetIds.length > 0) {
              for (const assetId of workerAssetIds) {
                addAssignment(workerId, workflowId, assetId);
              }
            } else {
              addAssignment(workerId, workflowId);
            }
          }
        }
      } else {
        const assetIds = data.assetIds && data.assetIds.length > 0 ? data.assetIds : [undefined];
        for (const workerId of data.workerIds) {
          for (const workflowId of data.workflowIds) {
            for (const assetId of assetIds) {
              addAssignment(workerId, workflowId, assetId);
            }
          }
        }
      }

      const results = await Promise.allSettled(createPromises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;

      await loadData();
      setSummaryLoaded(false); // refresh summary data on next switch
      setIsModalOpen(false);

      if (duplicateCount > 0 || failedCount > 0) {
        const messages = [];
        if (successCount > 0) messages.push(`${successCount} assignment(s) created.`);
        if (duplicateCount > 0) messages.push(`${duplicateCount} skipped (already assigned).`);
        if (failedCount > 0) messages.push(`${failedCount} failed.`);
        toast.info(messages.join(' '));
      }
    } catch (err) {
      console.error('Failed to create assignments:', err);
      toast.error('Failed to create some assignments. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleEditAssignment = async (
    assignmentId: string,
    data: { workerId?: string; dueDate?: string },
  ) => {
    await assignmentService.updateAssignment(assignmentId, data);
    await loadData();
    setSummaryLoaded(false);
  };

  const handleCopyAssignment = async (data: {
    workflowId: string;
    workerId: string;
    assetId?: string;
    dueDate?: string;
  }) => {
    await assignmentService.createAssignment({
      workflowId: data.workflowId,
      workerId: data.workerId,
      assetId: data.assetId,
      dueDate: data.dueDate,
    });
    await loadData();
    setSummaryLoaded(false);
  };

  // ---- Manager entry for modals ----

  const managerEntry = user ? [{
    id: user.id,
    email: user.email,
    fullName: user.fullName + ' (You)',
    role: user.role as 'owner' | 'worker' | 'admin',
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    activeAssignments: 0,
    coverageCount: assets.length,
    theme: user.theme,
  }] : [];

  const teamMembersForModal = [
    ...managerEntry,
    ...teamMembers.map(m => ({
      id: m.id,
      email: m.email,
      fullName: m.fullName,
      role: 'worker' as const,
      createdAt: m.createdAt,
      updatedAt: m.createdAt,
      activeAssignments: m.activeAssignments,
      coverageCount: m.coverageCount,
      theme: 'light' as const,
    })),
  ];

  // ---- Sort header helper ----

  const renderSortHeader = (field: SortField, label: string, extraClass?: string) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 transition-colors ${extraClass || ''}`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <FontAwesomeIcon
          icon={sortField === field ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          className={`w-3 h-3 ${sortField === field ? 'text-primary-500' : 'text-neutral-300'}`}
        />
      </span>
    </th>
  );

  // ---- Active filter pill labels ----

  const filterPills: { key: keyof Filters; label: string }[] = [];
  if (filters.search) filterPills.push({ key: 'search', label: `"${filters.search}"` });
  if (filters.status) filterPills.push({ key: 'status', label: filters.status.replace('_', ' ') });
  if (filters.workflowId) {
    const wf = uniqueWorkflows.find(w => w.value === filters.workflowId);
    filterPills.push({ key: 'workflowId', label: wf?.label || 'Workflow' });
  }
  if (filters.workerId) {
    const wk = uniqueAssignees.find(a => a.value === filters.workerId);
    filterPills.push({ key: 'workerId', label: wk?.label || 'Assignee' });
  }
  if (filters.assetId) {
    const as_ = uniqueAssets.find(a => a.value === filters.assetId);
    filterPills.push({ key: 'assetId', label: as_?.label || 'Asset' });
  }

  const removeFilter = (key: keyof Filters) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  // ---- Render ----

  if (loading) {
    return (
      <div className="h-full bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (assignments.length === 0 && !hasActiveFilters) {
    return (
      <div className="h-full flex flex-col bg-neutral-50">
        <PageTopBar title="Dashboard" />
        <div className="flex-1 overflow-y-auto">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <EmptyState
            title={isOwner ? 'Create your first assignment' : 'No assignments yet'}
            description={
              isOwner
                ? 'Assign workflows to team members to start tracking progress.'
                : 'Your manager will assign workflows when there is work to be done.'
            }
            action={
              isOwner
                ? { label: 'New Assignment', onClick: () => setIsModalOpen(true) }
                : undefined
            }
          />
        </div>
        </div>

        {/* Modals still need to render even in empty state */}
        {isOwner && (
          <NewAssignmentModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleCreateAssignments}
            teamMembers={teamMembersForModal}
            workflows={workflows}
            assets={assets}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar title="Dashboard" />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

        {/* Search + Filters + Mode Toggle */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <SearchBar
              value={filters.search}
              onChange={value => setFilters(prev => ({ ...prev, search: value }))}
              placeholder="Search workflows, assignees, assets..."
            />

            <SearchableSelect
              value={filters.status}
              onChange={v => setFilters(prev => ({ ...prev, status: v }))}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              placeholder="All Statuses"
            />

            <SearchableSelect
              value={filters.workflowId}
              onChange={v => setFilters(prev => ({ ...prev, workflowId: v }))}
              options={uniqueWorkflows}
              placeholder="All Workflows"
            />

            {isOwner && (
              <SearchableSelect
                value={filters.workerId}
                onChange={v => setFilters(prev => ({ ...prev, workerId: v }))}
                options={uniqueAssignees}
                placeholder="All Assignees"
              />
            )}

            <SearchableSelect
              value={filters.assetId}
              onChange={v => setFilters(prev => ({ ...prev, assetId: v }))}
              options={uniqueAssets}
              placeholder="All Assets"
            />

            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear Filters
              </Button>
            )}

            {isOwner && (
              <div className="ml-auto">
                <Button variant="primary" size="sm" onClick={() => setIsModalOpen(true)}>
                  New Assignment
                </Button>
              </div>
            )}
          </div>

          {/* Active filter pills */}
          {filterPills.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <span>Filtered:</span>
              {filterPills.map(pill => (
                <span
                  key={pill.key}
                  className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full cursor-pointer hover:bg-primary-100"
                  onClick={() => removeFilter(pill.key)}
                >
                  {pill.label} &times;
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Workflow groups */}
        <div className="space-y-6">
          {workflowGroups.size === 0 ? (
            <EmptyState
              title="No Results"
              description="No assignments match your filters."
              action={{ label: 'Clear Filters', onClick: () => setFilters(EMPTY_FILTERS) }}
            />
          ) : (
            Array.from(workflowGroups.entries()).map(([wfId, group]) => {
              const isCollapsed = collapsedGroups.has(wfId);
              const sorted = sortAssignments(group.assignments);
              const summaryWidgets = summaryLoaded
                ? getSummaryWidgets(group.assignments[0])
                : [];

              return (
                <div key={wfId}>
                  {/* Group header */}
                  <button
                    onClick={() => toggleCollapse(wfId)}
                    className="w-full flex items-center gap-2 mb-2 text-left group"
                  >
                    <svg
                      className={`w-4 h-4 text-neutral-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <h3 className="text-sm font-semibold text-neutral-700 group-hover:text-neutral-900 transition-colors">
                      {group.workflowName}
                    </h3>
                    <span className="text-xs text-neutral-400">
                      {group.assignments.length} assignment{group.assignments.length !== 1 ? 's' : ''}
                    </span>
                  </button>

                  {/* Table */}
                  {!isCollapsed && (
                    <Card variant="default" className="overflow-x-auto no-scrollbar">
                      <table className="min-w-full">
                        <thead className="bg-neutral-50">
                          <tr>
                            {/* ── FIXED COLUMNS ── */}
                            {renderSortHeader('assignee', 'Assignee')}
                            {renderSortHeader('asset', 'Asset')}
                            {renderSortHeader('progress', 'Progress')}
                            {renderSortHeader('due', 'Due')}
                            {isOwner && <th className="px-3 py-3 w-10" />}

                            {/* ── DIVIDER ── */}
                            <th className="w-px px-0 bg-neutral-200" />

                            {/* ── DETAILS COLUMNS ── */}
                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                              Return 1D
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                              Return 2D
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                              Return 5D
                            </th>

                            {/* ── DIVIDER ── */}
                            {summaryWidgets.length > 0 && (
                              <th className="w-px px-0 bg-neutral-200" />
                            )}

                            {/* ── SUMMARY COLUMNS ── */}
                            {summaryWidgets.map(sw => (
                              <th
                                key={sw.widgetId}
                                className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider"
                              >
                                {sw.title}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                          {sorted.map(assignment => {
                            const info = detailsProgressMap.get(assignment.id);
                            const progress = info?.progress || 0;

                            return (
                              <tr
                                key={assignment.id}
                                className="hover:bg-neutral-50 transition-colors cursor-pointer"
                                onClick={() => navigate(`/task/${assignment.id}`)}
                              >
                                {/* ── FIXED ── */}
                                {/* Assignee */}
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-neutral-900">
                                  {assignment.worker?.fullName || '-'}
                                </td>

                                {/* Asset */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {assignment.asset ? (
                                    <div>
                                      <div className="text-sm font-medium text-neutral-900">
                                        {assignment.asset.name}
                                      </div>
                                      <div className="text-xs text-neutral-500">
                                        {assignment.asset.sector}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-neutral-400">-</span>
                                  )}
                                </td>

                                {/* Progress + steps */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <div className="w-20 bg-neutral-200 rounded-full h-1.5">
                                        <div
                                          className={`h-1.5 rounded-full ${getProgressColor(progress)}`}
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                      <span className="text-xs font-medium text-neutral-600">{progress}%</span>
                                    </div>
                                    <div className="text-[10px] text-neutral-400 mt-0.5">
                                      {info?.currentStep || 'Not Started'}
                                    </div>
                                  </div>
                                </td>

                                {/* Due Date */}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500">
                                  {formatDate(assignment.dueDate)}
                                </td>

                                {/* Actions (owner) */}
                                {isOwner && (
                                  <td className="px-3 py-3 whitespace-nowrap text-right">
                                    <button
                                      onClick={e => { e.stopPropagation(); openMenu(assignment.id, e.currentTarget); }}
                                      className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                      </svg>
                                    </button>
                                  </td>
                                )}

                                {/* ── DIVIDER ── */}
                                <td className="w-px px-0 bg-neutral-200" />

                                {/* ── DETAILS ── */}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500">
                                  —
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500">
                                  —
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500">
                                  —
                                </td>

                                {/* ── DIVIDER ── */}
                                {summaryWidgets.length > 0 && (
                                  <td className="w-px px-0 bg-neutral-200" />
                                )}

                                {/* ── SUMMARY ── */}
                                {summaryWidgets.map(sw => (
                                  <td
                                    key={sw.widgetId}
                                    className="px-4 py-3 whitespace-nowrap text-center"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    {renderSummaryCell(assignment.id, sw, assignment)}
                                  </td>
                                ))}

                              </tr>
                            );
                          })}
                          {sorted.length === 0 && (
                            <tr>
                              <td colSpan={20} className="px-4 py-8 text-center text-sm text-neutral-400">
                                No assignments
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Card>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      </div>

      {/* Kebab menu (portalled) */}
      {openMenuId && (() => {
        const assignment = assignments.find(a => a.id === openMenuId);
        if (!assignment) return null;
        return createPortal(
          <div
            ref={menuRef}
            className="fixed w-36 bg-surface border border-neutral-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <button
              onClick={() => { setEditingAssignment(assignment); setOpenMenuId(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Edit
            </button>
            <button
              onClick={() => { setCopyingAssignment(assignment); setOpenMenuId(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Duplicate
            </button>
            <div className="border-t border-neutral-100 my-1" />
            <button
              onClick={() => { setDeletingAssignmentId(assignment.id); setOpenMenuId(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-danger-600 hover:bg-danger-50"
            >
              Delete
            </button>
          </div>,
          document.body,
        );
      })()}

      {/* Peek modal (summary mode) */}
      {peekModal && (() => {
        const { assignmentId, widget, assignment } = peekModal;
        return (
          <div className="fixed inset-0 z-50 flex flex-col">
            <div className="absolute inset-0 bg-black/60" onClick={() => setPeekModal(null)} />
            <div className="relative flex-1 flex flex-col m-4 md:m-8 bg-surface rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 shrink-0">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">
                    {assignment.workflow?.name} — {widget.title}
                  </h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {assignment.worker?.fullName || 'Unknown'}
                    {assignment.asset?.name ? ` · ${assignment.asset.name}` : ''}
                    {' · '}
                    <span className="capitalize">{assignment.status.replace('_', ' ')}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/task/${assignmentId}?step=${widget.stepId}&dashboard=open`}
                    className="text-xs font-medium text-primary-600 hover:text-primary-800"
                    onClick={() => setPeekModal(null)}
                  >
                    Open Full View
                  </Link>
                  <button
                    onClick={() => setPeekModal(null)}
                    className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Dashboard iframe */}
              <div className="flex-1 min-h-0">
                <iframe
                  src={`/task/${assignmentId}?step=${widget.stepId}&dashboard=open&embed=true`}
                  className="w-full h-full border-0"
                  title="Task Dashboard Preview"
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modals */}
      {isOwner && (
        <>
          <NewAssignmentModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleCreateAssignments}
            teamMembers={teamMembersForModal}
            workflows={workflows}
            assets={assets}
          />

          <EditAssignmentModal
            isOpen={editingAssignment !== null}
            onClose={() => setEditingAssignment(null)}
            onSave={handleEditAssignment}
            assignment={editingAssignment}
            teamMembers={teamMembersForModal}
            workflows={workflows}
          />

          <CopyAssignmentModal
            isOpen={copyingAssignment !== null}
            onClose={() => setCopyingAssignment(null)}
            onCopy={handleCopyAssignment}
            assignment={copyingAssignment}
            teamMembers={teamMembersForModal}
            workflows={workflows}
            assets={assets}
          />

          <ConfirmationModal
            isOpen={deletingAssignmentId !== null}
            onClose={() => setDeletingAssignmentId(null)}
            onAction={async () => {
              if (!deletingAssignmentId) return;
              try {
                await assignmentService.deleteAssignment(deletingAssignmentId);
                await loadData();
                setSummaryLoaded(false);
              } catch (err: any) {
                toast.error(err.response?.data?.detail || 'Failed to delete assignment');
              }
            }}
            title="Delete Assignment"
            description="Are you sure you want to delete this assignment?"
            actionText="Delete"
          />
        </>
      )}
    </div>
  );
};

export default UnifiedDashboard;
